import { Container, Graphics } from "pixi.js"
import { TileMapMetaProvider } from "game-common/tile_map_meta_provider"
import { FLORA_TILESET, FLOWER_PATCH_TILES, TRANSPARENT_SMALL_FLOWER_TILES } from "game-common/room/flora"
import { TileSize } from "game-common/models"
import { CLASSIC_GRASS_TILE, DAPPLED_GRASS_START, FOREST_GRASS_TILE_COUNT, FOREST_GRASS_BLEND_COUNT } from "game-common/room/ember/grass_art"
import { forestDirtEdgePosition, groundTileAt, onForestDirtEdgeSoil } from "./dirt_ground"

/** Resolve live map edits too: soil or flooring painted over grass must stop the effect. */
export const grassAt = (provider: TileMapMetaProvider, px: number, py: number): boolean => {
    if (!provider) return false
    const x = Math.floor(px / TileSize), y = Math.floor(py / TileSize)
    const tile = (layer: string): number => groundTileAt(provider, x, y, layer)
    const overlay = tile("map2")
    if (overlay) {
        const sheet = provider.resolveTileSet(String(overlay))
        const flower = sheet?.tilesetId === FLORA_TILESET &&
            [...FLOWER_PATCH_TILES, ...TRANSPARENT_SMALL_FLOWER_TILES].includes(overlay - sheet.firstGid)
        // A forest-dirt edge is transparent on its grass side: the grass under it is what shows there.
        const dirtEdge = forestDirtEdgePosition(provider, overlay)
        if (!flower && (dirtEdge === undefined || onForestDirtEdgeSoil(dirtEdge, px, py) !== false)) return false
    }
    const gid = tile("map1")
    if (!gid) return false
    const sheet = provider.resolveTileSet(String(gid))
    if (!sheet) return false
    const position = gid - sheet.firstGid
    return (sheet.tilesetId === "forest-grass" && position >= 0 && position < FOREST_GRASS_BLEND_COUNT) ||
        (sheet.tilesetId === "lpc-terrain" && position === CLASSIC_GRASS_TILE) ||
        (sheet.tilesetId === "authored-terrain" && position >= DAPPLED_GRASS_START &&
            position < DAPPLED_GRASS_START + FOREST_GRASS_TILE_COUNT)
}

interface Contact { x: number; y: number; scale: number; zIndex: number; alpha: number; moving: boolean; angle?: number; travelAngle?: number }
interface Feet { art: Graphics; x: number; y: number; distance: number; side: number; stepMs: number }
interface Patch { art: Graphics; x: number; y: number; scale: number; lean: number; born: number; alpha: number }
const RECOVERY_MS = 280
const MAX_PATCHES = 96

/** Across the stride, compressed vertically for the game's three-quarter view. */
export const grassSoles = (angle: number, scale: number) => {
    const dx = Math.cos(angle), dy = Math.sin(angle)
    const sideView = Math.abs(dx) > Math.abs(dy)
    const acrossX = -dy * 3.5 + (sideView ? dx : 0)
    const acrossY = sideView ? dx * 1.75 : 0
    return [-1, 1].map(side => ({
        x: acrossX * side * scale, y: acrossY * side * scale,
        size: sideView && acrossY * side < 0 ? 0.75 : 1,
    }))
}

/** Tiny pixel blades in front of the soles; footfalls leave briefly bent blades on the ground.
 * Driven by rendered displacement so interpolated NPCs and players share the same effect.
 * All art lives in the sorted world surface, with no filters, textures, timers, or server events.
 */
export class GrassFeet {
    private feet = new Map<string, Feet>()
    private seen = new Set<string>()
    private patches: Patch[] = []
    private roomId?: string

    constructor(private surface: Container, private onGrass: (x: number, y: number) => boolean) {}

    beginFrame(roomId: string) {
        if (this.roomId !== roomId) {
            this.clear()
            this.roomId = roomId
        }
        this.seen.clear()
    }

    contact(id: string, contact: Contact, now = performance.now()) {
        const { x, y, scale, zIndex, alpha, moving } = contact
        const angle = Number.isFinite(contact.angle) ? contact.angle : Math.PI / 2
        const lean = -Math.cos(Number.isFinite(contact.travelAngle) ? contact.travelAngle : angle)
        const soles = grassSoles(angle, scale)
        const left = this.onGrass(x + soles[0].x, y + soles[0].y)
        const right = this.onGrass(x + soles[1].x, y + soles[1].y)
        if (!left && !right) { this.remove(id); return }
        this.seen.add(id)
        let feet = this.feet.get(id)
        if (!feet) {
            feet = { art: this.art(), x, y, distance: 0, side: -1, stepMs: now - 150 }
            this.feet.set(id, feet)
        }
        const distance = Math.hypot(x - feet.x, y - feet.y)
        // Teleports and re-entry do not stamp a backlog of footsteps.
        if (moving && distance < 32) feet.distance += distance
        else feet.distance = 0
        if (feet.distance >= 9 * scale && now - feet.stepMs >= 90) {
            const side = feet.side
            if ((side < 0 ? left : right) && this.patches.length < MAX_PATCHES) {
                const art = this.art()
                art.zIndex = zIndex - 0.01
                art.alpha = alpha * 0.7
                const sole = soles[side < 0 ? 0 : 1]
                this.patches.push({ art, x: x + sole.x, y: y + sole.y,
                    scale: scale * sole.size, lean, born: now, alpha })
            }
            feet.side = -side
            feet.distance = 0
            feet.stepMs = now
        }
        feet.x = x; feet.y = y
        feet.art.clear()
        feet.art.position.set(Math.round(x), Math.round(y))
        feet.art.zIndex = zIndex + 0.01
        feet.art.alpha = alpha * 0.85
        const bend = moving ? Math.max(0, 1 - (now - feet.stepMs) / RECOVERY_MS) : 0
        if (left) this.blades(feet.art, soles[0].x, soles[0].y, scale * soles[0].size, feet.side === 1 ? bend : 0, lean)
        if (right) this.blades(feet.art, soles[1].x, soles[1].y, scale * soles[1].size, feet.side === -1 ? bend : 0, lean)
    }

    endFrame(now = performance.now()) {
        for (const id of this.feet.keys()) if (!this.seen.has(id)) this.remove(id)
        this.patches = this.patches.filter(patch => {
            const age = (now - patch.born) / RECOVERY_MS
            if (age >= 1 || !this.onGrass(patch.x, patch.y)) { patch.art.destroy(); return false }
            patch.art.clear()
            patch.art.position.set(Math.round(patch.x), Math.round(patch.y))
            this.blades(patch.art, 0, 0, patch.scale, 1 - age, patch.lean)
            patch.art.alpha = patch.alpha * 0.65 * (1 - age)
            return true
        })
    }

    remove(id: string) { this.feet.get(id)?.art.destroy(); this.feet.delete(id) }
    clear() {
        for (const id of this.feet.keys()) this.remove(id)
        this.patches.forEach(patch => patch.art.destroy())
        this.patches = []
    }
    private art() { const art = new Graphics(); this.surface.addChild(art); return art }

    private blades(art: Graphics, x: number, y: number, scale: number, bend: number, side: number) {
        const colors = [0x438c39, 0x81b64e, 0x559847]
        // Roots sit slightly in front of the sole, on the ground. Only blade tips cover the shoe.
        const ground = Math.round(y + 2 * scale)
        art.beginFill(0x327d3b, 0.65)
        art.drawRect(Math.round(x - 3 * scale), ground - 1, Math.max(1, 7 * scale), 1)
        art.endFill()
        for (let i = 0; i < 3; i++) {
            const root = Math.round(x + (i - 1) * 2 * scale)
            const height = Math.max(1, Math.round((i === 1 ? 3 : 2) * scale * (1 - bend * 0.65)))
            const lean = Math.round(bend * side * 2 * scale)
            art.beginFill(colors[i])
            art.drawRect(root, ground - 1, Math.max(1, scale), 1)
            art.drawRect(root + lean, ground - height, Math.max(1, scale), height)
            art.endFill()
        }
    }
}
